import type { NormalizedItem, TeachingEpisodeSource } from "./types";

export class TeachingExclusionUnavailableError extends Error {
  constructor() {
    super("Authoritative teaching exclusion is unavailable");
    this.name = "TeachingExclusionUnavailableError";
  }
}

/**
 * Runs only after the ledger group has been classified from the original fetch.
 * An injected source means teaching is enabled, so source failure or an id-less
 * item in a conversation known to contain an episode fails closed to no text.
 */
export async function excludeTeachingItems(
  source: TeachingEpisodeSource | null,
  conversationId: string,
  items: NormalizedItem[],
): Promise<NormalizedItem[]> {
  if (!source) return items;
  let coverage: Awaited<ReturnType<TeachingEpisodeSource["coverage"]>>;
  try {
    coverage = await source.coverage(conversationId);
  } catch {
    // The pipeline treats this as a retryable, watermark-fencing skip. Returning
    // [] here would hide the content from the model but incorrectly consume its
    // cursor provenance, permanently losing a conversation after a transient or
    // narrowly attributed malformed-row failure.
    throw new TeachingExclusionUnavailableError();
  }
  if (!coverage.hasTeachingEpisodes) return items;
  if (items.some((item) => typeof item.id !== "string" || !/^msg_[A-Za-z0-9_-]+$/.test(item.id))) {
    return [];
  }
  const withheld = new Set(coverage.teachingItemIds);
  return items.filter((item) => !withheld.has(item.id ?? ""));
}
